import { Component, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TrpcService } from '../../services/trpc/trpc.service';
import { MessageService, FilterService } from 'primeng/api';
import { FormBuilder } from '@angular/forms';
import { Table } from 'primeng/table';
import { Identifier, SUREPARKSubscriber } from '../../../../../express-tRPC/src/shared/types';

@Component({
  selector: 'app-surepark-member-ids-report',
  templateUrl: './surepark-member-ids-report.component.html',
  styleUrls: ['./surepark-member-ids-report.component.scss'],
  providers: [MessageService]
})
export class SUREPARKMemberIDsReportComponent implements OnInit, AfterViewInit, OnDestroy {
  sureparkMembers: SUREPARKSubscriberWithFormattedIdentifiers[] = [];
  locationId: string = '';
  @ViewChild('dt') table!: Table;
  loading: boolean = true;
  @ViewChild('globalSearchInput', { static: false }) globalSearchInput!: ElementRef;
  private destroy$ = new Subject<void>();

  statusFilterValues: string[] = [];

  constructor(
    private trpcService: TrpcService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadReportData();

    // Subscribe to location changes
    this.trpcService.currentLocationObservable.subscribe((location: string) => {
      this.locationId = location;
    });
  }

  ngAfterViewInit() {
    // Setup debounced search
    if (this.globalSearchInput) {
      fromEvent(this.globalSearchInput.nativeElement, 'input')
        .pipe(
          debounceTime(250), // Wait 250ms after the last event before emitting
          distinctUntilChanged(), // Only emit if value is different from previous value
          takeUntil(this.destroy$)
        )
        .subscribe((event: any) => {
          if (this.table) {
            this.table.filterGlobal(event.target.value, 'contains');
          }
        });
    }
  }

  async loadReportData() {
    this.loading = true;

    try {
      const sureparkMembersResult: SUREPARKSubscriber[] | void = await this.trpcService.trpc.surepark.getSureparkGroupsSubscribers.query();

      if (sureparkMembersResult) {
        this.sureparkMembers = sureparkMembersResult.map((member: SUREPARKSubscriber) => {
          const formattedIdentifiers = this.formatIdentifiers(member.identifiers || []);
          return {
            ...member,
            formattedIdentifiers
          };
        });

        // Extract unique status values for the filter
        this.statusFilterValues = Array.from(
          new Set(
            this.sureparkMembers
              .map(member => member.status?.toString() || '')
              .filter(status => status !== '')
          )
        );
      }
    } catch (error: any) {
      this.messageService.add({
        severity: "error",
        detail: error.message,
        key: 'general-message'
      });
    } finally {
      this.loading = false;
    }
  }

  formatIdentifiers(identifiers: Identifier[]): string[] {
    return identifiers.map(identifier => {
      return `${identifier.type}: ${identifier.identifierNumber || 'N/A'}`;
    });
  }

  // Custom filter function to search through formatted identifiers
  filterGlobal(value: string, matchMode: string) {
    if (this.table) {
      this.table.filterGlobal(value, matchMode);

      // If no results are found with the default filter, check identifiers
      if (this.table.filteredValue && this.table.filteredValue.length === 0 && value) {
        this.table.filteredValue = this.sureparkMembers.filter(member =>
          member.formattedIdentifiers.some(identifier => identifier.toLowerCase().includes(value.toLowerCase()))
        );
      }
    }
  }

  /**
   * Exports the current table data to a CSV file
   * Includes all data that matches current filters
   */
  exportCSV() {
    try {
      // Get the filtered data from the table
      const data = this.table.filteredValue || this.sureparkMembers;

      if (data.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'No Data',
          detail: 'There is no data to export',
          key: 'general-message'
        });
        return;
      }

      // Define CSV headers
      const headers = ['First Name', 'Last Name', 'Start Validity', 'End Validity', 'Status', 'Identifiers'];

      // Format the data for CSV
      const csvData = data.map(member => {
        return [
          member.firstName || '',
          member.lastName || '',
          member.startValidity ? new Date(member.startValidity).toLocaleString() : '',
          member.endValidity ? new Date(member.endValidity).toLocaleString() : '',
          member.status || '',
          member.formattedIdentifiers.join('; ')
        ];
      });

      // Create CSV content
      const csvContent = [headers, ...csvData].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');

      // Create a download link and trigger the download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `surepark-members-${new Date().toISOString().slice(0, 10)}.csv`);
      link.click();
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Export Failed', detail: 'Failed to export data', key: 'general-message' });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

interface SUREPARKSubscriberWithFormattedIdentifiers extends SUREPARKSubscriber {
  formattedIdentifiers: string[];
}
